import React, { useEffect, useState } from "react";
import {
  Table,
  Input,
  Button,
  Popconfirm,
  Form,
  Space,
  Col,
  Row,
  message,
  Tag,
  Select,
  Switch,
} from "antd";
import { useDispatch, useSelector } from "react-redux";
import { PlusOutlined } from "@ant-design/icons";
import { ReactComponent as CrossIcon } from "../../../assets/cross.svg";
import { ReactComponent as EditIcon } from "../../../assets/edit-icon.svg";
import { ReactComponent as DeleteIcon } from "../../../assets/delete-icon.svg";
import { ReactComponent as SaveIcon } from "../../../assets/save-icon.svg";
import SpinnerComponent from "../../../components/spinner";
import { useNavigate } from "react-router-dom";
import {
  createUserLab,
  deleteUserLab,
  setEditLabPermissions,
  updateUserLab,
  fetchAllUserLabs,
} from "../redux/actions";
import _ from "lodash";
import { fetchAllLabs } from "../../LabSetup/redux/actions";
import { fetchLabStatuses } from "../../LabStatuses/redux/actions";
import { useParams } from "react-router-dom";
import UseSupAdminOrAdminWithUserManagerPermission from "../../../components/useSupAdminOrAdminWithUserManagerPermission";
import TablePermissionsSection from "./tablePermissionSection";
import {
  userLabPermissions,
  initialEditingRowData,
  handlePermissionsCol,
} from "./utils";
import AMultiSelect from "../../../components/AMultiSelect";

const LabPermissionsTable = ({ isProfileCase, canUpdate }) => {
  const params = useParams();
  const [sortedInfo, setSortedInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editingLoading, setEditingLoading] = useState(false);
  const [editingKey, setEditingKey] = useState("");
  const [data, setData] = useState([]);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [page, setPage] = useState(() => 1);
  const [resultPerPage, setResultPerPage] = useState(() => 10);
  const [req, setReq] = useState(false);
  const [search, setSearch] = useState({});
  const [searchedData, setSearchedData] = useState([]);
  const [defaultPageOneReq, setDefaultPageOneReq] = useState(false);
  const { Option } = Select;
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  let canEditEveryThing = UseSupAdminOrAdminWithUserManagerPermission();
  const [permissions, setPermissions] = useState({ ...userLabPermissions });
  const [editingRowData, setEditingRowData] = useState({
    ...initialEditingRowData,
  });
  const [statusCheckedList, setStatusCheckedList] = useState([]);
  const [labStatuses, setLabStatuses] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState(null);

  const handlePermissionChange = (section, key, value) => {
    setPermissions(prev => ({
      ...prev,
      [section]: {
        ...(prev[section] || {}),
        [key]: value
      }
    }));
  };

  const isEditing = (record) => {
    return record.id === editingKey;
  };

  const {
    fetchingLoading,
    createLabLoading,
    isEditLabPermissions,
    currentUserObj,
    user_labs,
  } = useSelector((state) => state.userManagerReducer);
  const userId = params?.id || currentUserObj?.id;

  const { allLabs } = useSelector((state) => state.labsSetupReducer);

  const { allLabStatuses } = useSelector(
    (state) => state.labStatusesReducer
  );

  // Fetch lab statuses when component mounts
  useEffect(() => {
    dispatch(fetchLabStatuses());
  }, [dispatch]);

  // Fetch user labs when component mounts or when userId changes
  useEffect(() => {
    if (userId) {
      // Transform search parameters to match backend expectations
      const apiSearch = {};
      
      if (search.contactNumber) {
        apiSearch.contactNumber = search.contactNumber;
      }
      if (search.labName) {
        apiSearch.labName = search.labName;
      }
      if (search.statusId) {
        apiSearch.labStatusId = search.statusId;
      }
      
      // Prepare sort parameters for the API
      const sortParams = {};
      if (sortedInfo?.order) {
        // Map column keys to API field names
        const fieldMapping = {
          "labSetupId": "contactNumber",
          "labName": "labName"
        };
        
        // Get the correct field name for the API
        const apiField = fieldMapping[sortedInfo.columnKey] || sortedInfo.columnKey;
        
        // Map Ant Design sort orders to API sort directions
        const apiOrder = sortedInfo.order === "ascend" ? "asc" : "desc";
        
        sortParams.orderBy = apiField;
        sortParams.orderDirection = apiOrder;
      }
      
      dispatch(fetchAllUserLabs({ 
        userId,
        search: apiSearch,
        sortedInfo,
        page,
        resultPerPage: 10, // Show all records
        ...sortParams // Include the sort parameters
      }));
    }

    return () => {
      dispatch(setEditLabPermissions(false));
    };
  }, [userId, page, resultPerPage, req, JSON.stringify(search), sortedInfo]); // Added sortedInfo to dependencies

  const [debouncedCallApi] = useState(() =>
    _.debounce(() => setReq((prev) => !prev), 1000)
  );

  const [debouncedDefaultPageOneCallApi] = useState(() =>
    _.debounce(() => setDefaultPageOneReq((prev) => !prev), 1000)
  );

  // Update lab statuses when allLabStatuses changes
  useEffect(() => {
    if (allLabStatuses && allLabStatuses.length > 0) {
      const updatedStatuses = allLabStatuses.map(({ id, statusName }) => ({
        value: id,
        label: statusName
      }));
      setLabStatuses(updatedStatuses);
    }
  }, [allLabStatuses]);

  useEffect(() => {
    setLoading(fetchingLoading);
  }, [fetchingLoading]);

  useEffect(() => {
    dispatch(fetchAllLabs({ search: {}, perPage: 1000 }));
  }, []);

  const onChangeSelect = (value, option) => {
    const item = allLabs.filter((el) => el.id == option.value)[0];

    const { labName } = item;
    setSelectedItem(item);
    form.setFieldsValue({
      labSetupId: value, // Set labSetupId for backend
      labName,
    });
    // setExpandedRowKeys([option.key]);
  };
  useEffect(() => {
    setEditingLoading(createLabLoading);
    if (!createLabLoading) {
      setEditingKey("");
      setExpandedRowKeys([]);
    }
  }, [createLabLoading]);

  useEffect(() => {
    setData(user_labs);
    setSearchedData(user_labs);
  }, [user_labs]);

  const EditableCell = ({
    editing,
    dataIndex,
    title,
    inputType,
    record,
    index,
    children,
    ...restProps
  }) => {
    const inputNode = (
      <Select
        style={{
          width: "100%",
        }}
        // disabled={isProfileCase}
        readOnly={isProfileCase && !canUpdate}
        className={isProfileCase && !canUpdate ? "profile-case-selector" : ""}
        dropdownClassName="select-dropdown-custom"
        getPopupContainer={() =>
          document.getElementsByClassName(
            "ant-layout-content layout-content site-layout-background custom-textarea"
          )[0]
        }
        showSearch
        placeholder="Select Lab"
        optionFilterProp="children"
        onChange={onChangeSelect}
        optionLabelProp="label"
        filterOption={(input, option) => {
          let str = option.children
            .slice(0, option.children.length - 2)
            .join("");
          return str.toLowerCase().indexOf(input.toLowerCase()) >= 0;
        }}
      >
        {allLabs?.map(({ id, labName }) => {
          const mapIds = searchedData.map((pro) => pro.labSetupId);
          return (
            <Option
              key={id}
              value={id}
              label={labName}
              disabled={mapIds.includes(id)}
            >
              {labName || ""}{" "}
              {mapIds.includes(id) && "(Added)"}
            </Option>
          );
        })}
      </Select>
    );

    const labNameInput = <Input className="labname-input" readOnly />;

    return (
      <td title={title} {...restProps} className={editing ? "editing-row" : ""}>
        {editing ? (
          <Form.Item
            name={dataIndex}
            style={{
              margin: 0,
            }}
            rules={[
              {
                required: dataIndex === "labSetupId" || dataIndex === "labName" ? true : false,
                message: `Required!`,
              },
            ]}
          >
            {dataIndex === "labSetupId" || dataIndex === "labName" ? (
              isProfileCase && !canUpdate ? (
                record?.labSetup?.labName
              ) : (
                record?.isAdd ? inputNode : record?.labSetup?.labName
              )
            ) : dataIndex?.includes("statusName") ? (
              <Tag
                color={selectedItem?.lab_status?.color}
                style={{
                  color: "#231f20",
                  width: "100px",
                  height: "20px",
                  textAlign: "center",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {selectedItem?.lab_status?.statusName || ""}
              </Tag>
            ) : (
              labNameInput
            )}
          </Form.Item>
        ) : (
          children
        )}
      </td>
    );
  };

  const handleChange = (pagination, filters, sorter) => {
    // Update sortedInfo with the column key and order
    setSortedInfo(sorter);
    
    // Reset to page 1 when sorting changes
    setPage(1);
    
    // Trigger API call with new sorting parameters
    debouncedDefaultPageOneCallApi();
  };

  const edit = (record) => {
    dispatch(setEditLabPermissions(true));
    form.setFieldsValue({
      ...record,
      labSetupId: record.labSetupId || record.labSetup?.id,
      labName: record.labSetup?.labName || "",
      key: record.id,
    });
    setSelectedItem(record.lab || record.labSetup);
    const {
      ...rest
    } = record;

    if (!Boolean(record?.isAdd)) {
      let updateKeyData = searchedData.map((el) => ({ ...el, key: el.id }));
      setSearchedData(updateKeyData);
    }

    setEditingRowData({
      ...editingRowData,
    });
    setPermissions((prev) => ({
      ...prev,
      ...record.permissions,
    }));
    setEditingKey(record.id);
    setExpandedRowKeys([record.id]);
  };

  const cancel = (record) => {
    // setEditingKey("");
    dispatch(setEditLabPermissions(false));
    if (record?.isAdd) {
      setSearchedData((data) => data.filter((el) => el.id !== record.id));
    }
    form.resetFields();
    setEditingKey("");
    setPermissions({ ...userLabPermissions });
    setExpandedRowKeys([]);
  };

  const save = async (id) => {
    try {
      const row = await form.validateFields();
      const index = searchedData.findIndex((item) => id === item.id);
      const newData = [...searchedData];

      if (index > -1) {
        const item = searchedData[index];
        if (item?.isAdd) {
          form.submit();
          return; // Exit early for new items
        }
        
        // Prepare update data
        const updateData = {
          id,
          userId: item.userId,
          labSetupId: row.labSetupId || item.labSetupId,
          ...editingRowData,
          permissions: { ...permissions },
        };
        
        // Create updated item for local state
        const updatedItem = {
          ...item,
          ...row,
          ...updateData,
          lab: selectedItem || item.lab,
          permissions: { ...permissions },
        };
        
        // Update local state immediately
        const updatedSearchedData = searchedData.map((item, idx) => 
          idx === index ? updatedItem : item
        );
        
        const updatedData = data.map(item => 
          item.id === id ? updatedItem : item
        );
        
        setSearchedData(updatedSearchedData);
        setData(updatedData);
        
        // Clear editing state
        setExpandedRowKeys([]);
        setEditingKey("");
        dispatch(setEditLabPermissions(false));
        
        // Dispatch update action
        dispatch(
          updateUserLab({ 
            updateData,
            setExpandedRowKeys: () => {},
            setEditingKey: () => {}
          })
        );
        
      } else {
        newData.push(row);
        setData(newData);
        setSearchedData(newData);
        setEditingKey("");
      }
    } catch (errInfo) {
      console.log("Validate Failed:", errInfo);
      message.error("Failed to save changes. Please try again.");
    }
  };
  const handleDelete = (record) => {
    setEditingKey(record.id);
    const { userId } = record;
    let shouldLSUpdate = userId == userId;
    dispatch(deleteUserLab({ ...record, shouldLSUpdate }));
  };

  const handleAdd = () => {
    setPermissions({ ...userLabPermissions });
    form.resetFields();
    dispatch(setEditLabPermissions(true));
    let id = Math.floor(Math.random() * 1122123456);
    const newData = {
      id,
      name: "",
      isAdd: true,
      key: id,
    };
    setEditingRowData({ ...initialEditingRowData });
    setSearchedData([newData, ...searchedData]);
    setEditingKey(id);
    setExpandedRowKeys([id]);
    edit({ ...newData, ...initialEditingRowData });
    setSelectedItem(null);
  };

  const handleFinish = (values) => {
    const { labSetupId } = values;
    
    // Only include the fields we want to send
    const payload = {
      labSetupId,
      userId: currentUserObj.id,
      permissions: { ...permissions }
    };

    dispatch(createUserLab(payload));
  };

  const searchInput = (key) => (
    <div>
      <Input
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => {
          const newSearch = { ...search };
          if (e.target.value) {
            newSearch[key] = e.target.value;
          } else {
            delete newSearch[key];
          }
          setSearch(newSearch);
          debouncedDefaultPageOneCallApi();
        }}
        value={search[key] || ''}
        allowClear
        style={{ 
          border: '1px solid #d9d9d9',
          borderRadius: '2px',
          height: '25px',
          lineHeight: '25px',
          padding: '0 8px',
          fontSize: '13px',
          width: '100%'
        }}
        bordered={false}
        className="search-input"
      />
    </div>
  );
  
  // LabName-side filtering fallback (if needed)
  const searchFunc = (searchParams, data) => {
    if (!searchParams || Object.keys(searchParams).length === 0) return data;
    
    return data.filter(item => {
      return Object.entries(searchParams).every(([key, value]) => {
        if (!value) return true;
        
        const searchValue = value.toString().toLowerCase();
        
        switch (key) {
          case 'labName':
            return item.labSetup?.labName?.toLowerCase().includes(searchValue) || '';
          case 'statusName':
            return item.labSetup?.lab_status?.statusName?.toLowerCase().includes(searchValue) || '';
          default:
            return true;
        }
      });
    });
  };
  const handleNavigate = (id) =>
    navigate(`/settings/users-manager/update-user/${id}`);

  const handleSelectOnchange = (value, options, key, setHandler) => {
    if (key === 'status') {
      // Allow multiple selections for status filter
      if (value.includes("all")) {
        setStatusCheckedList([...options.map((item) => item.value)]);
        setSearch(prev => ({
          ...prev,
          statusId: options.map((item) => item.value)
        }));
      } else if (value.includes("not-all")) {
        setStatusCheckedList([]);
        const { statusId, ...rest } = search;
        setSearch(rest);
      } else {
        setStatusCheckedList(value);
        setSearch(prev => ({
          ...prev,
          statusId: value
        }));
      }
    } else {
      // Handle other multi-select filters
      if (value.includes("all")) {
        setHandler([...options.map((item) => item.value)]);
        value = options.map((item) => item.value);
      } else if (value.includes("not-all")) {
        setHandler([]);
        value = [];
      } else {
        setHandler(value);
      }
      handleMultiSelect(value, key);
    }
  };

  const handleMultiSelect = (list, key) => {
    setSearch({
      ...search,
      [key]: list,
    });
  };

  const columns = [
    {
      title: () => {
        return (
          <div align="top" className="select-parent-div">
            <div className="special-table-header-text permission-table"
              style={{
                paddingLeft: "8px"
              }}
            >
              Lab Name
            </div>
            <div>{searchInput("labName")}</div>
          </div>
        );
      },
      dataIndex: "labSetupId",
      key: "labName",
      sorter: true,
      sortOrder: sortedInfo?.columnKey === "labName" && sortedInfo?.order,
      width: "22%",
      editable: true,
      render: (id, record) => record?.labSetup?.labName,
    },
    {
      title: () => {
        return (
          <div align="top" className="select-parent-div">
            <div className="special-table-header-text permission-table"
              style={{
                paddingLeft: "11px"
              }}
            >
              Permissions
            </div>
            <div style={{ marginRight: "15px" }}>
              <Input disabled style={{ border:"none", backgroundColor:"transparent" }}/>
            </div>
          </div>
        );
      },
      dataIndex: "permissions",
      key: "permissions",
      sortOrder: sortedInfo?.columnKey === "permissions" && sortedInfo?.order,
      width: "30%",
      editable: false,
      render: (permissions, record) => {
        if (record?.isAdd) {
          return null;
        }
        
        // If we have permissions in the record, use those
        const permissionData = permissions || {};
        
        // Map of section names to their display names
        const sectionNames = {
          general: 'General',
        };
        
        // Array to hold all permission strings
        const permissionStrings = [];
        
        // Process each section
        Object.entries(permissionData).forEach(([section, perms]) => {
          if (typeof perms === 'object' && perms !== null) {
            const sectionName = sectionNames[section] || section;
            const enabledPerms = [];
            
            // Get enabled permissions for this section
            Object.entries(perms).forEach(([key, value]) => {
              if (value === true) {
                // Format the permission key for display (e.g., 'view' -> 'View')
                const formattedKey = key
                  .split('_')
                  .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                  .join(' ');
                enabledPerms.push(formattedKey);
              }
            });
            
            // Add section with its permissions
            if (enabledPerms.length > 0) {
              permissionStrings.push({
                section: sectionName,
                perms: enabledPerms
              });
            }
          }
        });
        
        // Add special roles if they exist
        const roles = [];
        if (record.document_control_manager) {
          roles.push('Document Control Manager');
        }
        if (record.lab_manager) {
          roles.push('Lab Manager');
        }
        
        if (roles.length > 0) {
          permissionStrings.push({
            section: 'Roles',
            perms: roles
          });
        }
        
        if (permissionStrings.length === 0) {
          return 'No permissions set';
        }
        
        // Render each section with its permissions on a single line
        return (
          <div className="checkbox-div" style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {permissionStrings.map(({ section, perms }, index) => (
              <div key={index}>
                {section} - {perms.join(', ')}
              </div>
            ))}
          </div>
        );
      },
    },
    {
      title: () => {
        return (
          <div align="top" style={{ width: "100%" }}>
            <div className="special-table-header-text"
              style={{
                paddingLeft: "30px"
              }}
            >
              Status
            </div>
            <div style={{ width: "100%" }}>
              <AMultiSelect
                dropdownClassName="status-selector"
                debouncedCallApi={debouncedDefaultPageOneCallApi}
                disabled={false}
                placeholder=""
                selectTags={statusCheckedList}
                onSelectChange={(value) =>
                  handleSelectOnchange(
                    value,
                    labStatuses,
                    "status",
                    setStatusCheckedList
                  )
                }
                data={labStatuses}
                filterOption={(input, option) => {
                  return (
                    String(option?.label)
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  );
                }}
              />
            </div>
          </div>
        );
      },
      dataIndex: ["lab", "labStatus", "statusName"],
      key: "statusName",
      sortOrder: sortedInfo?.columnKey === "statusName" && sortedInfo?.order,
      ellipsis: true,
      width: "5%",
      align: "center",
      editable: true,
      render: (status, record, id) => {
        const labStatus = record?.labSetup?.labStatus;
        var color = labStatus?.color || '#d9d9d9';
        const name = labStatus?.statusName || 'No Status';

        if (name === "Active") {
          color = "#a7e0a5";
        } else if (name === "Inactive") {
          color = "#fcb9b8";
        }

        return (
          <Tag
            color={color}
            style={{
              color: "#383838",
              width: "80px",
              // height: "22px",
              borderRadius: "10px",
              textAlign: "center",
              fontSize: "12px",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              cursor: 'pointer',
            }}
          >
            {name}
          </Tag>
        );
      },
    },
    {
      title: "",
      key: "action",
      dataIndex: "",
      width: "5%",
      align: "center",
      render: (_, record) => {
        if (!canUpdate && !isProfileCase) {
          return;
        }

        const editable = isEditing(record);
        return editable ? (
          editingLoading && editingKey === record.id ? (
            <SpinnerComponent fontSize={14} />
          ) : (
            <Space size={15} align="center">
              <SaveIcon
                className="custom-icon"
                fill="#ed017f"
                onClick={() => save(record.id)}
              />
              <Popconfirm
                title="Are you sure you want to cancel?"
                onConfirm={() => cancel(record)}
                okText="Yes"
                cancelText="No"
              >
                <CrossIcon fill="#ed017f !important" className="custom-icon" />
              </Popconfirm>
            </Space>
          )
        ) : (
          <Space size={15} align="center">
            <EditIcon
              className={`custom-icon ${
                isEditLabPermissions && "disable-icon"
              }`}
              fill="#ed017f"
              disabled={editingKey !== ""}
              onClick={() => !isEditLabPermissions && edit(record)}
            />
            {data.length >= 1 &&
              (isEditLabPermissions && canUpdate ? (
                <DeleteIcon
                  fill="#ed017f"
                  className={`custom-icon disable-icon`}
                />
              ) : (
                canUpdate && (
                  <Popconfirm
                    title="Are you sure you want to delete this Lab?"
                    onConfirm={() => handleDelete(record)}
                    okText="Yes"
                    cancelText="No"
                  >
                    <DeleteIcon fill="#ed017f" className={`custom-icon`} />
                  </Popconfirm>
                )
              ))}
          </Space>
        );
      },
    },
  ];
  const mergedColumns = columns.map((col) => {
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      onCell: (record) => {
        let title = col.title;
        if (col.key === "statusName") {
          title = record?.lab?.lab_status?.statusName;
        }
        return {
          record,
          inputType: "text",
          dataIndex: col.dataIndex,
          title: title,
          editing: isEditing(record),
        };
      },
    };
  });

  const handleStatusChange = (statusId) => {
    setSelectedStatus(statusId);
    // Update search params and trigger refetch
    setSearch(prev => ({
      ...prev,
      statusId: statusId
    }));
  };

  const expandedRowRender = (record) => {
    if (expandedRowKeys.includes(record.id)) {
      let values = form.getFieldsValue();

      let shouldDisabled =
        values?.labSetupId === undefined ||
        !canUpdate ||
        (!canUpdate && isProfileCase)
          ? true
          : false;

      return (
        <Row>
          <TablePermissionsSection
            isProfileCase={isProfileCase}
            permissions={permissions}
            handlePermissionChange={handlePermissionChange}
            disabledCheckBox={shouldDisabled}
            labStatus={selectedItem?.lab_status?.statusName}
          />
        </Row>
      );
    }
  };

  return (
    <Row>
      <Col span={24} className="heading-col" style={{ marginBottom: "0" }}>
        <Row align="middle" gutter={[30, 0]}>
          <Col>
            <div>
              <span className="heading-span user-details-heading">Lab Permissions</span>
            </div>
          </Col>
          <Col>
            {canUpdate && (
              <Button
                icon={<PlusOutlined />}
                className="add-activity-btn lab-button"
                size="middle"
                disabled={isEditLabPermissions}
                onClick={handleAdd}
              >
                LAB
              </Button>
            )}
          </Col>
        </Row>
      </Col>

      <Col span={24}>
        <Form form={form} component={false} onFinish={handleFinish}>
          <Table
            className="special-table  table lab-permission-table"
            components={{
              body: {
                cell: EditableCell,
              },
            }}
            expandable={{
              expandedRowRender,
              expandIcon: null,
              showExpandColumn: false,
              expandedRowKeys: expandedRowKeys,
            }}
            onRow={({ id }) =>
              expandedRowKeys.includes(id) && {
                className: "expand-parent",
              }
            }
            scroll={{ x: true }}
            rowClassName={(record) => `editable-row ${record.isAdd ? 'new-added-row' : ''}`}
            dataSource={searchedData}
            // dataSource={data}
            loading={loading}
            columns={mergedColumns}
            onChange={handleChange}
            pagination={false}
            // rowKey={expandedRowKeys?.length > 0 ? expandedRowKeys[0].toString() : null}
          />
        </Form>
      </Col>
    </Row>
  );
};

export default LabPermissionsTable;
